import React from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  TableContainer, 
  Paper, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Chip,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Container,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import { fetchWithAuth } from '../utils/api';

const API_URL = '/api';

function LogsView({ user }) {
    const [logs, setLogs] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    
    const [logUsers, setLogUsers] = React.useState([]);
    const [filteredUserId, setFilteredUserId] = React.useState('');
    const [filteredAction, setFilteredAction] = React.useState('');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [startDate, setStartDate] = React.useState('');
    const [endDate, setEndDate] = React.useState('');
    const [showForcedOnly, setShowForcedOnly] = React.useState(false);

    const fetchLogs = () => {
        setLoading(true);
        setError('');
        
        const params = new URLSearchParams();
        if (filteredUserId) params.append('userId', filteredUserId);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (showForcedOnly) params.append('forced', 'true');
        
        const url = `${API_URL}/logs?${params.toString()}`;

        fetchWithAuth(url)
            .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed to fetch logs.')))
            .then(data => {
                // Apply local filtering for Action and Search Term (since backend might not support all)
                let filteredData = data;
                if (filteredAction) {
                    filteredData = filteredData.filter(log => log.action === filteredAction);
                }
                if (searchTerm) {
                    const term = searchTerm.toLowerCase();
                    filteredData = filteredData.filter(log => 
                        JSON.stringify(log.details).toLowerCase().includes(term) || 
                        log.action.toLowerCase().includes(term)
                    );
                }
                setLogs(filteredData);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    };
    
    const fetchLogUsers = () => {
        fetchWithAuth(`${API_URL}/logs/users`)
            .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed to fetch users.')))
            .then(data => setLogUsers(data))
            .catch(err => console.error(err.message));
    };

    React.useEffect(() => {
        fetchLogs();
        fetchLogUsers();
    }, []);

    const getActionColor = (action) => {
        if (action.includes('create')) return 'success';
        if (action.includes('delete')) return 'error';
        if (action.includes('update')) return 'info';
        if (action.includes('send')) return 'secondary';
        return 'default';
    };

    const formatActionName = (action) => {
        return action.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const renderLogDetails = (log) => {
        const { action, details } = log;
        const renderLine = (label, value) => value ? (
            <Typography variant="body2" sx={{ mb: 0.2 }}>
                <span style={{ color: '#888', fontWeight: 600, marginRight: '4px' }}>{label}:</span> 
                <span style={{ fontWeight: 500 }}>{value}</span>
            </Typography>
        ) : null;

        switch (action) {
            case 'create_event':
                const ev = details.event || {};
                return (
                    <Box>
                        {renderLine("Day", ev.Day)}
                        {renderLine("Time", ev.Time)}
                        {renderLine("Grade", ev.Grade)}
                        {renderLine("Subject", ev.Subject)}
                        {renderLine("Teacher", ev.Teacher)}
                        {renderLine("Room", ev.Room)}
                    </Box>
                );
            case 'update_event':
                const from = details.from || {};
                const to = details.to || {};
                const fields = Object.keys(to).filter(k => k !== 'Forced' && from[k] !== to[k]);
                return (
                    <Box>
                        <Box sx={{ mb: 1, pb: 1, borderBottom: '1px dashed #eee' }}>
                            {renderLine("Target", `${from.Day} | ${from.Time} | G${from.Grade}`)}
                            {renderLine("Subject", from.Subject)}
                        </Box>
                        {fields.map(k => (
                            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#aaa', minWidth: '60px' }}>{k.toUpperCase()}</Typography>
                                <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>{from[k] || 'None'}</Typography>
                                <ArrowForwardIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>{to[k] || 'None'}</Typography>
                            </Box>
                        ))}
                    </Box>
                );
            case 'delete_event':
                const dev = details.deletedEvent || {};
                return (
                    <Box>
                        {renderLine("Day", dev.Day)}
                        {renderLine("Time", dev.Time)}
                        {renderLine("Grade", dev.Grade)}
                        {renderLine("Subject", dev.Subject)}
                    </Box>
                );
            case 'send_schedule_email':
                return (
                    <Box>
                        {renderLine("Recipient", details.recipient)}
                        {renderLine("Subject", details.subject)}
                    </Box>
                );
            default:
                return (
                    <Typography variant="caption" component="pre" sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1, display: 'block', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(details, null, 2)}
                    </Typography>
                );
        }
    };

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#ffffff' }}>
            {/* STICKY FILTERS */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 3, 
                    mb: 4, 
                    borderRadius: 4, 
                    border: '1px solid #e0e0e0', 
                    bgcolor: '#f8f9fa',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1000
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        <FormControl size="small" sx={{ width: '400px' }}>
                            <InputLabel>User</InputLabel>
                            <Select value={filteredUserId} label="User" onChange={(e) => setFilteredUserId(e.target.value)} sx={{ bgcolor: 'white' }}>
                                <MenuItem value=""><em>All Users</em></MenuItem>
                                {logUsers.map(u => <MenuItem key={u.userId} value={u.userId}>{u.userEmail}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <FormControl size="small" sx={{ width: '280px' }}>
                            <InputLabel>Action</InputLabel>
                            <Select value={filteredAction} label="Action" onChange={(e) => setFilteredAction(e.target.value)} sx={{ bgcolor: 'white' }}>
                                <MenuItem value=""><em>All Actions</em></MenuItem>
                                <MenuItem value="create_event">Create Event</MenuItem>
                                <MenuItem value="update_event">Update Event</MenuItem>
                                <MenuItem value="delete_event">Delete Event</MenuItem>
                                <MenuItem value="send_schedule_email">Send Email</MenuItem>
                                <MenuItem value="update_app_config">Settings Change</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <TextField
                            label="Search Details"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ bgcolor: 'white', width: '200px' }}
                            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> }}
                        />
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <TextField label="From" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white', width: '150px' }} />
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <TextField label="To" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white', width: '150px' }} />
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Button variant="contained" onClick={fetchLogs} sx={{ fontWeight: 700, px: 4, height: '40px' }}>Apply</Button>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={showForcedOnly} onChange={(e) => setShowForcedOnly(e.target.checked)} />}
                        label={<Typography variant="caption" fontWeight={700}>Forced Actions Only</Typography>}
                    />
                </Box>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : (
                <TableContainer component={Box}>
                    <Table sx={{ minWidth: 800, tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 800, color: 'text.secondary', borderBottom: '2px solid #f0f0f0', py: 2 } }}>
                                <TableCell width={180}>Timestamp</TableCell>
                                <TableCell width={220}>User</TableCell>
                                <TableCell width={180}>Action</TableCell>
                                <TableCell>Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map((log) => {
                                const logString = JSON.stringify(log.details);
                                const isForced = logString.includes('"Forced":true') || logString.includes('"Forced": true');
                                return (
                                    <TableRow 
                                        key={log._id} 
                                        sx={{ 
                                            '&:hover': { bgcolor: isForced ? '#fff9c4' : '#fcfcfc' },
                                            '& td': { py: 2.5, borderBottom: '1px solid #f5f5f5', verticalAlign: 'top' },
                                            bgcolor: isForced ? 'rgba(255, 152, 0, 0.08)' : 'inherit',
                                            borderLeft: isForced ? '6px solid #ed6c02' : 'none'
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {new Date(log.timestamp).toLocaleDateString()}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(log.timestamp).toLocaleTimeString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={log.userEmail} size="small" variant="outlined" sx={{ fontWeight: 500, maxWidth: '100%' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <Chip 
                                                    label={formatActionName(log.action)} 
                                                    size="small" 
                                                    color={getActionColor(log.action)}
                                                    sx={{ fontWeight: 700, borderRadius: 1 }}
                                                />
                                                {isForced && (
                                                    <Chip 
                                                        icon={<WarningAmberIcon style={{ color: 'white' }} />}
                                                        label="CONFLICT BYPASSED" 
                                                        size="small" 
                                                        sx={{ 
                                                            bgcolor: '#ed6c02', 
                                                            color: 'white', 
                                                            fontWeight: 900, 
                                                            fontSize: '0.65rem',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {renderLogDetails(log)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {logs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.disabled">No logs found matching filters.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default LogsView;
